import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

const DEFAULT_PLAY_STORE =
  'https://play.google.com/store/apps/details?id=in.restroai.lite';

const emptyForm = () => ({
  notificationEnabled: false,
  latestVersionName: '1.10.0',
  latestVersionCode: 68,
  releaseDate: '',
  message: 'Get the latest fixes, offline sync improvements, and new features for your shop.',
  playStoreUrl: DEFAULT_PLAY_STORE,
});

export default function RestroLiteAppReleasePanel() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(API_ENDPOINTS.RESTRO_LITE_APP_RELEASE);
      const d = res.data || {};
      setForm({
        notificationEnabled: Boolean(d.notificationEnabled),
        latestVersionName: d.latestVersionName || '1.10.0',
        latestVersionCode: Number(d.latestVersionCode) || 68,
        releaseDate: d.releaseDate ? String(d.releaseDate).slice(0, 10) : '',
        message: d.message || emptyForm().message,
        playStoreUrl: d.playStoreUrl || DEFAULT_PLAY_STORE,
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load app update settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (notificationEnabled) => {
    setBusy(true);
    setStatus('');
    setError('');
    try {
      const body = {
        notificationEnabled,
        latestVersionName: form.latestVersionName.trim(),
        latestVersionCode: Number(form.latestVersionCode),
        releaseDate: form.releaseDate || null,
        message: form.message.trim(),
        playStoreUrl: form.playStoreUrl.trim() || DEFAULT_PLAY_STORE,
      };
      const res = await axios.put(API_ENDPOINTS.RESTRO_LITE_APP_RELEASE, body);
      const d = res.data || {};
      setForm({
        notificationEnabled: Boolean(d.notificationEnabled),
        latestVersionName: d.latestVersionName || body.latestVersionName,
        latestVersionCode: Number(d.latestVersionCode) || body.latestVersionCode,
        releaseDate: d.releaseDate ? String(d.releaseDate).slice(0, 10) : '',
        message: d.message || body.message,
        playStoreUrl: d.playStoreUrl || DEFAULT_PLAY_STORE,
      });
      setStatus(
        notificationEnabled
          ? 'Update notification is live — PocketBillingAI users on older versions will see the popup when they open the app.'
          : 'Update notification disabled — no popup will be shown to shop owners.'
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save app update settings');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/80 to-white p-4 sm:p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">
            Android app updates
          </p>
          <h2 className="text-lg font-bold text-stone-900 mt-0.5">
            PocketBillingAI update notification
          </h2>
          <p className="text-sm text-stone-600 mt-1 max-w-2xl">
            Set the latest Play Store release. When enabled, shop owners on an older version see an
            update popup each time they open the app until they install the new version.
          </p>
        </div>
        <span
          className={`inline-flex shrink-0 self-start items-center rounded-full px-3 py-1 text-xs font-semibold ${
            form.notificationEnabled
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-stone-200 text-stone-600'
          }`}
        >
          {form.notificationEnabled ? 'Notifications ON' : 'Notifications OFF'}
        </span>
      </div>

      {loading ? (
        <p className="text-sm text-stone-500">Loading app release settings…</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-1">
            <span className="text-xs font-medium text-stone-600 uppercase tracking-wide">
              Latest update date
            </span>
            <input
              type="date"
              value={form.releaseDate}
              onChange={(e) => setForm((f) => ({ ...f, releaseDate: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
            />
          </label>

          <label className="block sm:col-span-1">
            <span className="text-xs font-medium text-stone-600 uppercase tracking-wide">
              Latest version name
            </span>
            <input
              type="text"
              placeholder="1.11.0"
              value={form.latestVersionName}
              onChange={(e) => setForm((f) => ({ ...f, latestVersionName: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
            />
          </label>

          <label className="block sm:col-span-1">
            <span className="text-xs font-medium text-stone-600 uppercase tracking-wide">
              Latest version code (Android build)
            </span>
            <input
              type="number"
              min={1}
              placeholder="69"
              value={form.latestVersionCode}
              onChange={(e) => setForm((f) => ({ ...f, latestVersionCode: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
            />
            <p className="text-xs text-stone-500 mt-1">
              From <code className="text-xs">android/app/build.gradle</code> — versionCode
            </p>
          </label>

          <label className="block sm:col-span-1">
            <span className="text-xs font-medium text-stone-600 uppercase tracking-wide">
              Play Store URL
            </span>
            <input
              type="url"
              value={form.playStoreUrl}
              onChange={(e) => setForm((f) => ({ ...f, playStoreUrl: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
            />
          </label>

          <label className="block sm:col-span-2">
            <span className="text-xs font-medium text-stone-600 uppercase tracking-wide">
              Popup message (optional)
            </span>
            <textarea
              rows={3}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm resize-y"
            />
          </label>
        </div>
      )}

      {!loading && (
        <div className="mt-5 flex flex-col sm:flex-row flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => save(true)}
            className="px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
          >
            {busy ? 'Saving…' : 'Send update notification'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => save(false)}
            className="px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 disabled:opacity-60"
          >
            Disable notifications
          </button>
        </div>
      )}

      {status && (
        <p className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          {status}
        </p>
      )}
      {error && (
        <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </p>
      )}
    </section>
  );
}
